<template>
  <div>
    <AccountingParentBar />
    <layout :isSuccess="true">
      <template v-slot:containerZone>
        <div class="my-4">
          <v-row class="bg-white mx-0">
            <v-col cols="12" class="py-15 px-5">
              <div class="flex items-center justify-between my-3">
                <h1 class="font-weight-bold">{{ $t('qr_payment.report_qr_payment') }}</h1>
                <div class="flex align-center gap-2" style="max-width: 900px;">
                  <v-menu
                    v-model="menuStartDate"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="$helper.datelabel(formData.startDate)"
                        :placeholder="$t('date')"
                        outlined
                        dense
                        hide-details
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :label="$t('from_date')"
                        prepend-inner-icon="mdi-calendar"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.startDate"
                      no-title
                      locale="th"
                      @input="menuStartDate = false"
                      :header-date-format="$helper.headerDateFormat"
                      :year-format="$helper.yearFormat"
                      :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth"
                    ></v-date-picker>
                  </v-menu>
                  <v-menu
                    v-model="menuEndDate"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="$helper.datelabel(formData.endDate)"
                        :placeholder="$t('date')"
                        outlined
                        dense
                        hide-details
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        :label="$t('to_date')"
                        prepend-inner-icon="mdi-calendar"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="formData.endDate"
                      no-title
                      locale="th"
                      @input="menuEndDate = false"
                      :header-date-format="$helper.headerDateFormat"
                      :year-format="$helper.yearFormat"
                      :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth"
                    ></v-date-picker>
                  </v-menu>
                  <v-btn @click="onSearch()" class="primary" :disabled="isLoading" elevation="0">
                    <v-icon>mdi-magnify</v-icon>
                    {{ $t('search') }}
                  </v-btn>
                  <v-btn @click="exportExcel()" class="blue white--text" :disabled="isLoading" elevation="0">
                    <v-icon class="me-2">mdi-file-excel-outline</v-icon>
                    Export Excel
                  </v-btn>
                </div>
              </div>
            </v-col>

            <v-col
              v-if="!isLoading"
              cols="12"
              class="px-3 pt-0 pb-4 lighten-4"
            >
              <v-data-table
                v-if="dataTable && dataTable.data"
                :headers="dataTable.headers"
                :items="dataTable.data"
                :items-per-page="10"
                class="elevation-1 data-table-qr"
                item-key="objectId"
                v-model="selectedReceipts"
                :show-select="isShowSelectPrintReceipt"
                :single-select="false"
                :no-data-text="$t('noinfo')"
                :no-results-text="$t('no_matching_records_found')"
              >

                <template v-slot:header.customerData="{header}"> 
                  <div class="text-nowrap-table text-center" style="min-width: 100px;">{{header.text}}</div>
                </template>
                <template v-slot:header.areasName="{header}">
                  <div class="text-nowrap-table text-center" style="min-width: 100px;">{{header.text}}</div>
                </template>
                <template v-slot:header.voidedByFullName="{header}">
                  <div class="text-nowrap-table text-center" style="min-width: 100px;">{{header.text}}</div>
                </template>

                <template v-slot:body.prepend>
                  <tr>
                    <td class="text-center">
                      <v-icon>
                        mdi-filter-multiple-outline
                      </v-icon>
                    </td>
                    <td class="ps-0">
                      <v-text-field v-model="filterData.objectId" type="text" :label="$t('qr_payment.reference_number')" solo dense hide-details></v-text-field>
                    </td>
                    <td class="ps-0">
                      <v-text-field v-model="filterData.ref1" type="text" label="Ref1" solo dense hide-details></v-text-field>
                    </td>
                    <td class="ps-0">
                      <v-text-field v-model="filterData.ref2" type="text" label="Ref2" solo dense hide-details></v-text-field>
                    </td>
                    <td class="ps-0">
                      <v-text-field v-model="filterData.customerMarketId" type="text" :label="$t('bill.customer_market_id')" solo dense hide-details></v-text-field>
                    </td>
                    <td class="ps-0">
                      <v-text-field v-model="filterData.customerData" type="text" :label="$t('customer.title')" solo dense hide-details></v-text-field>
                    </td>
                    <td class="ps-0">
                      <v-text-field v-model="filterData.areasName" type="text" :label="$t('bill.area')" solo dense hide-details></v-text-field>
                    </td>
                    <td class="ps-0">
                      <v-currency-field v-model="filterData.amount" :label="$t('bill.amount')" solo dense hide-details class="input-right input-disable-appearance" />
                    </td>
                    <td class="ps-0">
                      <v-text-field v-model="filterData.documentId" type="text" :label="$t('bill.document_id')" solo dense hide-details></v-text-field>
                    </td>
                    <td class="ps-0">
                      <v-select v-model="filterData.status" :items="filterOptions.status" type="text" :label="$t('bill.status')" solo dense hide-details clearable :no-data-text="$t('noinfo')"/>
                    </td>
                    <td class="ps-0">
                      <v-text-field v-model="filterData.receiptDocumentId" type="text" :label="$t('bill.receipt_id')" solo dense hide-details></v-text-field>
                    </td>
                    <td class="ps-0">
                      <v-menu
                        v-model="menuReconciledDate"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="filterDateLabel(filterData.reconciledTimeStamp)"
                            :label="$t('payment.confirmed_payment_date')"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            solo
                            dense
                            hide-details
                            :append-icon="filterData.reconciledTimeStamp ? 'mdi-close' : null"
                            @click:append="filterData.reconciledTimeStamp = null"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="filterData.reconciledTimeStamp"
                          @input="menuReconciledDate = false"
                          :header-date-format="$helper.headerDateFormat"
                          :year-format="$helper.yearFormat"
                          no-title
                          locale="th"
                          :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth"
                        ></v-date-picker>
                      </v-menu>
                    </td>
                    <td class="ps-0">
                      <v-select v-model="filterData.voidStatus" :items="filterOptions.voidStatus" type="text" :label="$t('payment.refund_payment')" solo dense hide-details clearable :no-data-text="$t('noinfo')" />
                    </td>
                    <td class="ps-0">
                      <v-menu
                        v-model="menuVoidedDate"
                        :close-on-content-click="false"
                        :nudge-right="40"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="filterDateLabel(filterData.voidedDate)"
                            :label="$t('payment.confirmed_payment_date')"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            solo
                            dense
                            hide-details
                            :append-icon="filterData.voidedDate ? 'mdi-close' : null"
                            @click:append="filterData.voidedDate = null"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="filterData.voidedDate"
                          @input="menuVoidedDate = false"
                          :header-date-format="$helper.headerDateFormat"
                          :year-format="$helper.yearFormat"
                          no-title
                          locale="th"
                          :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth"
                        ></v-date-picker>
                      </v-menu>
                    </td>
                    <td class="ps-0">
                      <v-text-field v-model="filterData.voidBy" type="text" :label="$t('payment.refund_payment_by')" solo dense hide-details></v-text-field>
                    </td>
                    <td class="ps-0"></td>
                  </tr>
                </template>
                <template v-slot:item.data-table-select="{ isSelected, select, item }">
                  <template v-if="isShowSelectPrintReceipt && item.isPaid">
                    <v-simple-checkbox
                      color="primary"
                      :value="isSelected"
                      @input="select($event)"
                    ></v-simple-checkbox>
                  </template>
                </template>

                <template v-slot:item.customerData="{ item }">
                  <div class="text-truncate text-center area-name">{{ item.customerData }}</div>
                </template>
                <template v-slot:item.areasName="{ item }">
                  <div class="text-truncate text-center area-name">{{ item.areasName }}</div>
                </template>

                <template v-slot:item.paymentTimeStamp="{ item }">
                  <template v-if="item.paymentTimeStamp">
                    {{ $moment(item.paymentTimeStamp).format("DD/MM/YYYY") }} <br />
                    {{ $moment(item.paymentTimeStamp).format("H:mm") }} {{ $t('timeunit.minute.short') }}
                  </template>
                </template>

                <template v-slot:item.createdAt="{ item }">
                  <template v-if="item.createdAt">
                    {{ $moment(item.createdAt).format("DD/MM/YYYY") }} <br />
                    {{ $moment(item.createdAt).format("H:mm") }}
                  </template>
                </template>

                <template v-slot:item.status="{ item }" class="text-center">
                  <template v-if="item.status == $t('payment.confirmed_payment_success')">
                    <v-chip
                      class="green--text"
                      color="rgb(209,242,225)"
                    >
                      <v-icon left x-small>mdi-circle</v-icon>
                      {{ item.status }}
                    </v-chip>
                  </template>
                  <template v-else>
                    <v-chip
                      class="red--text"
                      color="rgb(248,218,218)"
                    >
                      <v-tooltip top color="grey" content-class="black--text">
                        <template v-slot:activator="{ on, attrs }">
                          <div v-bind="attrs" v-on="on">
                            <v-icon left x-small>mdi-circle</v-icon>
                            {{ item.status }}
                          </div>
                        </template>
                        <span>{{ item.statusDetail }}</span>
                      </v-tooltip>
                    </v-chip>
                  </template>
                </template>

                <template v-slot:item.amount="{ item }">
                  <div class="text-right">{{ item.amount | currency }}</div>
                </template>

                <template v-slot:item.documentId="{ item }">
                  <template v-if="item.documentId">  
                    <a v-if="item.invoice" @click="modalInvoiceDialog(item.invoice)" class="underline">
                      {{ item.documentId }}
                    </a>
                    <a v-else-if="item.contractCheckout" @click="modalContractCheckoutDialog(item.contractCheckout)" class="underline">
                      {{ item.documentId }}
                    </a>
                    <a v-else-if="item.order" @click="modalOrderDialog(item.order)" class="underline">
                      {{ item.documentId }}
                    </a>
                  </template>
                </template>
                <template v-slot:item.receiptDocumentId="{ item }">
                  <a v-if="item.receiptDocumentId && item.receiptDocumentId != '-'" @click="modalReceiptDialog(item.receipt)" class="underline">
                    {{ item.receiptDocumentId }}
                  </a>
                  <span v-else>-</span>
                </template>
                <template v-slot:item.voidStatus="{ item }">
                  <div style="font-size: 13px;" v-if="item.voidedResult">
                    <template v-if="item.voidStatus == $t('payment.refund_payment_success')">
                      <span class="success--text">
                        <v-icon color="success">mdi-swap-horizontal</v-icon> {{ $t('payment.refund_payment_success') }}
                      </span>
                    </template>
                    <template v-else-if="item.voidStatus == $t('payment.refund_payment_failed')">
                      <span class="error--text">
                        <v-icon color="error">mdi-swap-horizontal</v-icon> {{ $t('payment.refund_payment_failed') }}
                      </span>
                    </template>
                  </div>
                  <template v-else>
                    <v-tooltip top color="grey" content-class="black--text">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="item.isDisableVoid ? on : null">
                          <v-btn
                            elevation="0"
                            color="warning"
                            class="px-2"
                            @click="modalQrPaymentVoidDialog(item)"
                            :disabled="item.isDisableVoid"
                          >
                            <v-icon v-if="!item.isDisableVoid">mdi-swap-horizontal</v-icon>
                            {{ $t('payment.refund') }}
                            <v-icon v-if="item.isDisableVoid" class="ms-1" size="14">mdi-information</v-icon>
                          </v-btn>
                        </div>
                      </template>
                      <span>{{ item.disableVoidDetail }}</span>
                    </v-tooltip>
                  </template>
                </template>
                <template v-slot:item.voidedDate="{ item }">
                  <template v-if="item.voidedDate">
                    {{ $moment(item.voidedDate).format("DD/MM/YYYY") }}
                    {{ $moment(item.voidedDate).format("H:mm") }} {{ $t('timeunit.minute.short') }}
                  </template>
                </template>
                <template v-slot:item.reconciledTimeStamp="{ item }">
                  <template v-if="item.reconciledTimeStamp">
                    {{ $moment(item.reconciledTimeStamp).format("DD/MM/YYYY") }}
                    {{ $moment(item.reconciledTimeStamp).format("H:mm") }} {{ $t('timeunit.minute.short') }}
                  </template>
                </template>
                <template v-slot:item.note="{ item }">
                  <div class="text-left">
                    <div v-if="item.voidedNote">{{ $t('account.user') }}: {{ item.voidedNote }}</div>
                    <div v-if="item.voidedResult" class="error--text">
                      <div v-if="item.voidedResult.errorDesc_th">{{ $t('account.system') }}: {{ item.voidedResult.errorDesc_th }}</div>
                      <div v-else-if="item.voidedResult.errorDesc">{{ $t('account.system') }}: {{ item.voidedResult.errorDesc }}</div>
                    </div>
                  </div>
                </template>

              </v-data-table>
            </v-col>
            <v-col v-else>
              <v-card class="d-flex justify-center py-10">
                <v-progress-circular
                  indeterminate
                  size="64"
                  color="green"
                ></v-progress-circular>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </template>
    </layout>
    <v-snackbar v-model="isError" color="error" top>
      <v-icon left size="18">mdi-information</v-icon>{{ $t('error') }}
    </v-snackbar>
    <InvoiceDialog
      v-model="isInvoiceDialog"
      :invoiceId="tempInvoiceId"
      :projectId="projectId"
    />
    <CheckoutPayment
      v-model="isContractCheckoutDialog"
      :objectId="tempContractCheckoutId"
      :isDisable="true"
    />
    <ReceiptDialog
      v-if="tempReceipt"
      v-model="isReceiptDialog" 
      :receiptId="tempReceipt.objectId"
      :projectId="projectId"
    />
    <QrPaymentVoidDialog
      v-model="isQrPaymentVoidDialog"
      :qrPaymentReport="tempQrPaymentReport"
      @onSuccess="onSearch()"
    />
    <OrderDialog
      v-model="isOrderDialog"
      :orderId="tempOrderId"
      :projectId="projectId"
    />
  </div>
</template>

<script>
import InvoiceDialog from "@/components/InvoiceDialog.component";
import SelectMonthYear from "@/components/SelectMonthYear.component";
import ParentBar from "@/components/ParentBar.vue";
import AccountingParentBar from "../../components/AccountingParentBar.component";
import Layout from "@/components/Layout.vue";
import { mapState } from "vuex";
import ReceiptDialog from "@/components/ReceiptDialog.component.vue";
import PrintDialog from "../../components/PrintDialog.component.vue";
import { ExportQRPayment } from "../../exports";
import CheckoutPayment from "../../components/CheckoutPayment.vue";
import QrPaymentVoidDialog from "../../components/QrPaymentVoidDialog.component";
import OrderDialog from "../../components/MeterDigitalOrderDetailDialog.vue";

export default {
  computed: {
    ...mapState({
      qrPaymentReports: (state) => state.qrPaymentReport.qrPaymentReports,
    }),
    qrPaymentReportData() {
      return this.qrPaymentReports.map(qrPaymentReport => {
        let obj = {}
        obj.objectId = qrPaymentReport.objectId
        obj.paymentTimeStamp = qrPaymentReport.paymentTimeStamp || ""
        obj.ref1 = qrPaymentReport.ref1 || ""
        obj.ref2 = qrPaymentReport.ref2 || ""
        obj.customerName = qrPaymentReport.customerName || ""
        obj.amount = qrPaymentReport.amount || 0
        obj.updatedReceiptId = qrPaymentReport.updatedReceiptId || ""
        obj.createdAt = qrPaymentReport.createdAt || ""
        obj.functionCode = qrPaymentReport.functionCode
        obj.marketName = qrPaymentReport.market.name || ""
        obj.voidedDate = qrPaymentReport.voidedDate || ""
        obj.voidedByFullName = qrPaymentReport.voidedByFullName || ""
        obj.voidedNote = qrPaymentReport.voidedNote || ""
        obj.voidedResult = qrPaymentReport.voidedResult || ""
        obj.reconciledTimeStamp = qrPaymentReport.reconciledTimeStamp || ""
        obj.statusDetail = ""
        obj.isDisableVoid = false
        obj.disableVoidDetail = ""
        obj.receiptDocumentId = '-'
        if (obj.voidedResult) {
          if (qrPaymentReport.isVoided) {
            obj.voidStatus = this.$t('payment.refund_payment_success')
          } else {
            obj.voidStatus = this.$t('payment.refund_payment_failed')
          }
        } else {
          obj.voidStatus = this.$t('payment.refund')
        }
        
        if(qrPaymentReport.isReceiptPaid){
          obj.status = this.$t('payment.confirmed_payment_success')
        }else{
          obj.status = this.$t('payment.confirmed_payment_failed')

          if(qrPaymentReport.amountMismatch){
            obj.statusDetail = this.$t('bill.amount_mismatch')
          }else if(qrPaymentReport.invoiceWasPaid){
            obj.statusDetail = this.$t('bill.invoice_was_paid')
          }else if(qrPaymentReport.invoiceNotSent){
            obj.statusDetail = this.$t('bill.invoice_in_maintaining')
          }else if(qrPaymentReport.invoiceNotFound){
            obj.statusDetail = this.$t('bill.invoice_not_found')
          }else{
            obj.statusDetail = this.$t('bill.payment_error') // bug createReceipt, payReceipt
          }
        }

        let startDisRefund = this.$moment({ hour: 23 })
        let endDisRefund = this.$moment(startDisRefund).add(3, 'hours')
        let currentTime = this.$moment()

        // ระหว่าง 23.00 - 2.00 ไม่สามารถคืนเงินได้ทุกกรณี
        if (currentTime.isBetween(startDisRefund, endDisRefund)) {
          obj.isDisableVoid = true
          obj.disableVoidDetail = this.$t('payment.refund_payment_disabled_time')
        } else {
          let paymentTime = this.$moment(qrPaymentReport.paymentTimeStamp)

          // กรณีจ่ายมาระหว่าง 23.00 - 2.00 จะนับเป็น 23.00 ของอีกวัน
          if (paymentTime.isBetween(
            this.$moment(paymentTime).startOf('day').add(23, 'hours'),
            this.$moment(paymentTime).startOf('day').add(26, 'hours')
          )) {
            paymentTime = this.$moment(paymentTime).add(1, 'days').startOf('day').add(23, 'hours')
          }

          // จ่ายก่อน 23.00 ไม่สามารถคืนเงินได้
          if (currentTime.isAfter(this.$moment(paymentTime).startOf('day').add(23, 'hours'))) {
            obj.isDisableVoid = true
            obj.disableVoidDetail = this.$t('payment.refund_payment_over_time')
          }
        }

        if(obj.functionCode == "00"){
          let invoice = qrPaymentReport.invoice
          let receipt = qrPaymentReport.receipt
          if(invoice){
            obj.customerData = invoice.customerData ? invoice.customerData.name : ""
            obj.customerMarketId = "-"
            if (invoice.contract) {
              obj.customerMarketId = invoice.contract.customerMarketId || ""
            } else if (invoice.booking) {
              obj.customerMarketId = invoice.booking.documentId || ""
            }
            obj.areasName = this.getAreaName(invoice.items) || "-"
            obj.documentId = invoice.documentId
            obj.invoice = invoice
            if (invoice.contractCheckouts) {
              obj.isDisableVoid = true
              obj.disableVoidDetail = this.$t('payment.refund_payment_checkout')
            }
          }
          if(receipt){
            obj.receiptDocumentId = receipt.documentId || "-"
            obj.receipt = receipt
          }
        } else if(obj.functionCode == "01") {
          let contractCheckout = qrPaymentReport.contractCheckout
          if(contractCheckout){
            obj.customerData = contractCheckout.customerData ? contractCheckout.customerData.name : ""
            obj.customerMarketId = contractCheckout.contract ? (contractCheckout.contract.customerMarketId || "") : ""
            obj.areasName = contractCheckout.areas.map(d => d.name).join(",") || "-"
            obj.documentId = contractCheckout.documentId || ""
            obj.contractCheckout = contractCheckout
            obj.receiptDocumentId = "-"
            obj.isDisableVoid = true
            obj.disableVoidDetail = this.$t('payment.refund_payment_checkout_detail')
          }
        } else if(obj.functionCode == "02"){
          let order = qrPaymentReport.order
          let receipt = qrPaymentReport.receipt
          if(order){
            obj.customerData = order.customerData ? order.customerData.name : ""
            obj.customerMarketId = "-"
            if (order.contract) {
              obj.customerMarketId = order.contract.customerMarketId || ""
            } else if (order.booking) {
              obj.customerMarketId = order.booking.documentId || ""
            }
            obj.areasName = this.getAreaName(order.items) || "-"
            obj.documentId = order.documentId || ""
            obj.order = order
          }
          if(receipt){
            obj.receiptDocumentId = receipt.documentId || "-"
            obj.receipt = receipt
          }
        }
        if (obj.isDisableVoid && obj.voidStatus == this.$t('payment.refund')) obj.voidStatus = this.$t('expired')
        return obj
      })
    },
    dataTable() {
      return {
        headers: [
        {
            text: this.$t('payment.payment_date'),
            value: "paymentTimeStamp",
            align: "center",
            width: '150px',
          },
          {
            text: this.$t('qr_payment.reference_number'),
            value: "objectId",
            width: '165px',
            filter: d =>
              this.filterData.objectId
              ? d.includes(this.filterData.objectId)
              : true
          },
          {
            text: "Ref1",
            value: "ref1",
            width: '150px',
            align: 'center',
            filter: d =>
              this.filterData.ref1
              ? d == d.includes(this.filterData.ref1)
              : d
          },
          {
            text: "Ref2",
            value: "ref2",
            width: '170px',
            align: 'center',
            filter: d =>
              this.filterData.ref2
              ? d.includes(this.filterData.ref2)
              : d
          },
          {
            text: this.$t('bill.customer_market_id'),
            value: "customerMarketId",
            width: '150px',
            align: 'center',
            filter: d =>
              this.filterData.customerMarketId
              ? d
                .toLowerCase()
                .includes(this.filterData.customerMarketId.toLowerCase())
              : d
          },
          {
            text: this.$t('customer.title'),
            value: "customerData",
            width: '200px',
            class: "custom-sort",
            align: 'center hd-resize',
            filter: d =>
              this.filterData.customerData
              ? d
                .toLowerCase()
                .includes(this.filterData.customerData.toLowerCase())
              : d
          },
          {
            text: this.$t('bill.area'),
            value: "areasName",
            width: "100px",
            class: "custom-sort",
            align: 'center hd-resize',
            filter: d =>
              this.filterData.areasName
              ? d
                .toLowerCase()
                .includes(this.filterData.areasName.toLowerCase())
              : d
          },
          {
            text: this.$t('bill.amount'),
            value: "amount",
            align: "center",
            width: '150px',
            filter: d =>
              this.filterData.amount
              ? d.toString().includes(this.filterData.amount)
              : d || d === 0
          },
          {
            text: this.$t('bill.document_id'),
            value: "documentId",
            width: '150px',
            filter: d =>
              this.filterData.documentId
              ? d
                .toLowerCase()
                .includes(this.filterData.documentId.toLowerCase())
              : true
          },
          {
            text: this.$t('bill.status'),
            value: "status",
            align: "center",
            width: '200px',
            filter: d =>
              this.filterData.status ? d == this.filterData.status : d
          },
          {
            text: this.$t('bill.receipt_id'),
            value: "receiptDocumentId",
            width: '150px',
            filter: (d) => {
              if (this.filterData.receiptDocumentId) {
                return d ? d.toLowerCase().includes(this.filterData.receiptDocumentId.toLowerCase()) : false;
              }
              return true;
            }
          },
          {
            text: this.$t('payment.confirmed_payment_date'),
            value: "reconciledTimeStamp",
            align: "center",
            width: "170px",
            filter: (d) =>
              this.filterData.reconciledTimeStamp
              ? this.$moment(d).format('DD/MM/YYYY') == this.$moment(this.filterData.reconciledTimeStamp).format('DD/MM/YYYY')
              : true,
          },
          {
            text: this.$t('payment.refund_payment'),
            value: "voidStatus",
            align: "center",
            width: "170px",
            filter: d => 
              this.filterData.voidStatus
              ? d == this.filterData.voidStatus
              : true
          },
          {
            text: this.$t('payment.confirmed_refund_date'),
            value: "voidedDate",
            align: "center",
            width: "170px",
            filter: (d) =>
              this.filterData.voidedDate
              ? this.$moment(d).format('DD/MM/YYYY') == this.$moment(this.filterData.voidedDate).format('DD/MM/YYYY')
              : true,
          },
          {
            text: this.$t('payment.refund_payment_by'),
            value: "voidedByFullName",
            class: "custom-sort",
            align: 'center hd-resize',
            width: "200px",
            filter: d =>
              this.filterData.voidBy
              ? d && d.toLowerCase().includes(this.filterData.voidBy.toLowerCase())
              : true
          },
          {
            text: this.$t('note'),
            value: "note",
            align: "left",
            width: "300px",
          }
        ],
        data: this.qrPaymentReportData,
      };
    },
  },
  data() {
    return {
      projectId: this.$route.params.projectId,
      marketId: this.$route.params.marketId,
      market: null,
      selectedMonthYear: null,
      isError: false,
      isLoading: false,
      isInvoiceDialog: false,
      tempInvoiceId: null,
      menuStartDate: false,
      menuEndDate: false,
      formData: {
        startDate: this.$moment({day: 1}).format("YYYY-MM-DD"),
        endDate: this.$moment().endOf("month").format("YYYY-MM-DD"),
      },
      filterData: {
        ref1: null,
        ref2: null,
        customerMarketId: null,
        customerData: null,
        areasName: null,
        amount: null,
        documentId: null,
        status: null,
        receiptDocumentId: null,
        reconciledTimeStamp: null,
        voidStatus: null,
        voidedDate: null,
        voidBy: null,
        objectId: null,
      },
      filterOptions: {
        status:[
          { text: this.$t('payment.confirmed_payment_failed')},
          { text: this.$t('payment.confirmed_payment_success')}
        ],
        voidStatus: [
          { text: this.$t('payment.refund_payment_success')},
          { text: this.$t('payment.refund_payment_failed')},
          { text: this.$t('expired')}
        ]
      },
      tempDate: null,
      isReceiptDialog: false,
      tempReceipt: null,
      isShowSelectPrintReceipt: false,
      selectedReceipts: [],
      isContractCheckoutDialog: false,
      tempContractCheckoutId: null,
      isQrPaymentVoidDialog: false,
      tempQrPaymentReport: null,
      isOrderDialog: false,
      tempOrderId: null,
      menuReconciledDate: false,
      menuVoidedDate: false,
    };
  },
  components: {
    InvoiceDialog,
    SelectMonthYear,
    ParentBar,
    AccountingParentBar,
    Layout,
    ReceiptDialog,
    PrintDialog,
    CheckoutPayment,
    QrPaymentVoidDialog,
    OrderDialog
  },
  async created() {
    try {
      this.onSearch();
    } catch (error) {
      this.isError = true;
    }
  },
  methods: {
    async initData() {
      try {
        this.tempDate = JSON.parse(JSON.stringify(this.formData));
        await this.$store.dispatch("qrPaymentReport/getQRPaymentReportBetweenDateByMarket", {
          marketId: this.marketId,
          startDate: this.$moment(this.formData.startDate).startOf("day").toISOString(),
          endDate: this.$moment(this.formData.endDate).endOf("day").toISOString(),
        });
      } catch (error) {
        throw error;
      }
    },
    async onSearch() {
      this.isLoading = true;
      try {
        await this.initData();
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.isError = true;
      }
    },
    async onSaveSuccess() {
      this.isLoading = true;
      try {
        await this.initData();
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        this.isError = true;
      }
    },
    modalReceiptDialog(receipt) {
      this.tempReceipt = receipt
      this.isReceiptDialog = true;
    },
    choosePrintReceipt() {
      this.selectedReceipts = [];
      this.isShowSelectPrintReceipt = true;
    },
    getAreaName(items) {
      let name = [];
      for (let item of items) {
        if (item.area) {
          name.push(item.area.name);
        }
      }
      return [...new Set(name)].join(",") || "-";
    },
    async exportExcel() {
      this.isLoading = true 
      try{
        await ExportQRPayment.exportQRPaymentReport(this.qrPaymentReportData, this.formData.startDate, this.formData.endDate)
        this.isLoading = false
      }catch(error){
        this.isError = true
        this.isLoading = false
      }
    },
    modalInvoiceDialog(invoice){
      this.tempInvoiceId = invoice.objectId
      this.isInvoiceDialog = true
    },
    modalContractCheckoutDialog(contractCheckout){
      this.tempContractCheckoutId = contractCheckout.objectId
      this.isContractCheckoutDialog = true
    },
    modalQrPaymentVoidDialog(qrPaymentReport) {
      this.tempQrPaymentReport = qrPaymentReport
      this.isQrPaymentVoidDialog = true
    },
    modalOrderDialog(order){
      this.tempOrderId = order.objectId
      this.isOrderDialog = true
    },
    filterDateLabel(date){
      if (date) {
        return this.$moment(date).format('DD MMM YYYY')
      } else {
        return ""
      }
    }
  },
};
</script>

<style lang="scss" scoped>
.data-table-qr::v-deep td.hd-resize {
  max-width: 100px;
}

.data-table-qr::v-deep th.custom-sort {
  position: relative;
  .v-icon{
    position: absolute;
    right: 24px;
    top: 18px;
  }
}
</style>